import type { CameraConfig } from '../config/gameConfig';
import type { ActionBus, GameAction } from './actions';

/**
 * Keyboard adapter, built on native DOM key events.
 *
 * Why not Phaser's KeyboardPlugin: in Phaser 3.90 it re-dispatches the whole
 * per-frame key queue on every new DOM key event and only de-duplicates
 * against the single previous event, so two quick presses inside one frame
 * are delivered as three (a toggle pressed twice ends up toggled once).
 * Native listeners fire exactly once per event. See docs/DECISIONS.md D16.
 *
 *   WASD / arrows  → continuous pan while held (frame-rate independent, via update())
 *   + / -          → zoom about the viewport centre
 *   Home           → reset view        Esc → cancel (leave build mode / clear selection)
 *   1–9, 0         → construction bar shortcuts
 *   G              → grid              P   → passability overlay
 *   Q army · X stop · B box mode · C colony · F attack-move · H stance (Aggressive ⇄ Hold)
 *   `              → debug overlay
 *   Space          → pause (offline)   .  → game speed ×1 / ×2 (offline)   (D125)
 */
const PAN_KEYS: Record<string, readonly [number, number]> = {
  arrowleft: [-1, 0], a: [-1, 0],
  arrowright: [1, 0], d: [1, 0],
  arrowup: [0, -1], w: [0, -1],
  arrowdown: [0, 1], s: [0, 1],
};

/** Discrete shortcuts: key (lower-case) → action. */
function shortcut(key: string, cam: CameraConfig): GameAction | null {
  switch (key) {
    case '+':
    case '=':
      return { type: 'zoom', factor: cam.buttonZoomStep, screen: null };
    case '-':
    case '_':
      return { type: 'zoom', factor: 1 / cam.buttonZoomStep, screen: null };
    case 'home':
      return { type: 'resetView' };
    case 'escape':
      return { type: 'cancel' };
    case 'enter':
      return { type: 'confirmBuild' };
    case 'c':
      return { type: 'toggleColony' };
    case 'q':
      return { type: 'selectArmy' };
    case 'x':
      return { type: 'stopUnits' };
    case 'b':
      return { type: 'toggleBoxMode' };
    case 'f':
      return { type: 'toggleAttackMove' };
    case 'h':
      return { type: 'setStance', stance: 'toggle' };
    case 'g':
      return { type: 'toggleGrid' };
    case 'p':
      return { type: 'togglePassability' };
    case '`':
      return { type: 'toggleDebug' };
    case ' ':
      return { type: 'togglePause' };
    case '.':
      return { type: 'cycleSpeed' };
    default:
      return /^[0-9]$/.test(key) ? { type: 'buildHotkey', key } : null;
  }
}

/** Number of KeyboardInput instances currently listening (diagnostics: must be 1). */
let attachedCount = 0;

export class KeyboardInput {
  private readonly held = new Set<string>();
  private attached = false;

  constructor(
    private readonly target: EventTarget,
    private readonly bus: ActionBus,
    private readonly camCfg: CameraConfig,
  ) {}

  static get attachedInstances(): number {
    return attachedCount;
  }

  /** Idempotent. */
  attach(): void {
    if (this.attached) return;
    this.attached = true;
    attachedCount++;
    this.target.addEventListener('keydown', this.onKeyDown);
    this.target.addEventListener('keyup', this.onKeyUp);
    this.target.addEventListener('blur', this.onBlur);
  }

  detach(): void {
    if (!this.attached) return;
    this.attached = false;
    attachedCount--;
    this.target.removeEventListener('keydown', this.onKeyDown);
    this.target.removeEventListener('keyup', this.onKeyUp);
    this.target.removeEventListener('blur', this.onBlur);
    this.held.clear();
  }

  /** Continuous panning while keys are held. */
  update(dtSeconds: number): void {
    if (this.held.size === 0) return;
    let x = 0;
    let y = 0;
    for (const k of this.held) {
      const v = PAN_KEYS[k];
      if (v) {
        x += v[0];
        y += v[1];
      }
    }
    x = Math.sign(x);
    y = Math.sign(y);
    if (x === 0 && y === 0) return;
    const len = Math.hypot(x, y);
    const step = this.camCfg.keyboardPanSpeed * dtSeconds;
    // Moving the view right means the world shifts left on screen.
    this.bus.emit({ type: 'pan', dx: (-x / len) * step, dy: (-y / len) * step });
  }

  private readonly onKeyDown = (e: Event): void => {
    const ke = e as KeyboardEvent;
    if (isTyping(ke) || ke.ctrlKey || ke.metaKey || ke.altKey) return;
    const key = ke.key.toLowerCase();
    if (PAN_KEYS[key]) {
      this.held.add(key);
      return;
    }
    if (ke.repeat) return; // auto-repeat should not re-toggle overlays
    const action = shortcut(key, this.camCfg);
    if (!action) return;
    if (key === ' ') ke.preventDefault(); // Space must not also press a focused HUD button
    this.bus.emit(action);
  };

  private readonly onKeyUp = (e: Event): void => {
    this.held.delete((e as KeyboardEvent).key.toLowerCase());
  };

  /** Releasing focus (alt-tab) would otherwise leave a pan key "stuck". */
  private readonly onBlur = (): void => {
    this.held.clear();
  };
}

function isTyping(e: KeyboardEvent): boolean {
  const t = e.target as { closest?: (s: string) => unknown } | null;
  return !!t && typeof t.closest === 'function' && !!t.closest('input, textarea, select, [contenteditable]');
}
